'use client'

import { default as MuxVideoComponent } from '@mux/mux-video-react'
import { PauseIcon, PlayIcon } from 'lucide-react'
import { useInView } from 'motion/react'
import { useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/utils'

type MuxVideoProps = {
	playbackId: string
	className?: string
	showAudioToggle?: boolean
	loop?: boolean
	minResolution?: any
} & (
	| {
			autoplay: 'hover' | true
			muted?: true
	  }
	| {
			autoplay?: false
			muted?: boolean
	  }
)

export default function MuxVideo({
	playbackId,
	className,
	autoplay = true,
	loop = true,
	showAudioToggle = true,
	minResolution = '720p'
}: MuxVideoProps) {
	const containerRef = useRef<HTMLDivElement>(undefined)
	const videoRef = useRef<HTMLVideoElement>(undefined)
	const shouldPlayRef = useRef(false)
	const [isReady, setIsReady] = useState(false)
	const [isHovered, setIsHovered] = useState(false)
	const [hasAudio, setHasAudio] = useState(true)
	const [isMuted, setIsMuted] = useState(autoplay !== false)
	const [isPlaying, setIsPlaying] = useState(autoplay !== false)
	const inView = useInView(containerRef, {
		amount: 0, // The amount of an element that should enter the viewport to be considered "entered". Either "some", "all" or a number between 0 and 1.
		margin: '0px', // A margin to add to the viewport to change the detection area. Use multiple values to adjust top/right/bottom/left, e.g. "0px -20px 0px 100px"
		initial: false, // Set an initial value to return until the element has been measured.
		once: false // Set to true to only trigger the animation once.
	})

	function checkForAudio(video: any) {
		return video.mozHasAudio || Boolean(video.webkitAudioDecodedByteCount) || Boolean(video.audioTracks)
	}

	useEffect(() => {
		if (!isReady) return

		const _hasAudio = checkForAudio(videoRef.current)
		setHasAudio(_hasAudio)
		// _hasAudio && console.log(`${playbackId} has audio`)
	}, [videoRef.current, isReady])

	// Mux renders the <video> without a source and attaches it asynchronously, so play()
	// rejects with NotSupportedError until then, and with AbortError if a pause interrupts it.
	const tryPlay = () => {
		videoRef.current?.play().catch(() => {})
	}

	const togglePlayPause = () => {
		if (isPlaying) {
			videoRef.current.pause()
		} else {
			tryPlay()
		}
	}

	const toggleSound = () => {
		// console.log(`toggleSound()`)
		if (isMuted) {
			videoRef.current.muted = false
		} else {
			videoRef.current.muted = true
		}
	}

	const readyHandler = () => {
		// console.log(`readyHandler:: ${playbackId} is ready`)
		setIsReady(true)
	}

	const playHandler = () => {
		// console.log(`playHandler:: ${playbackId} is playing`)
		setIsPlaying(true)
	}

	const pauseHandler = () => {
		// console.log(`pauseHandler:: ${playbackId} is paused`)
		setIsPlaying(false)
	}

	const endedHandler = () => {
		// console.log(`endedHandler:: ${playbackId} has ended`)
		if (loop) {
			tryPlay() // force fix occasional pausing on end issue
		}
	}

	const volumeChangeHandler = () => {
		// console.log(`volumeChangeHandler:: ${playbackId} is ${videoRef.current.muted ? 'muted' : 'unmuted'}`)
		setIsMuted(videoRef.current.muted)
	}

	// A play() that was requested before the source attached is retried here
	const canPlayHandler = () => {
		if (shouldPlayRef.current && videoRef.current?.paused) tryPlay()
	}

	useEffect(() => {
		if (!videoRef.current) return

		if (isMuted) {
			videoRef.current.muted = true
		} else {
			videoRef.current.muted = false
		}
	}, [isMuted])

	useEffect(() => {
		if (!videoRef.current) return

		shouldPlayRef.current = (autoplay === 'hover' && isHovered) || (autoplay === true && inView)

		if (shouldPlayRef.current) {
			tryPlay()
		} else {
			videoRef.current.pause()
		}
	}, [inView, isHovered, autoplay])

	useEffect(() => {
		const video = videoRef.current
		if (!video) return

		video.addEventListener('play', playHandler)
		video.addEventListener('pause', pauseHandler)
		video.addEventListener('ended', endedHandler)
		video.addEventListener('volumechange', volumeChangeHandler)
		video.addEventListener('loadeddata', readyHandler)
		video.addEventListener('loadedmetadata', canPlayHandler)
		video.addEventListener('canplay', canPlayHandler)

		return () => {
			video.removeEventListener('play', playHandler)
			video.removeEventListener('pause', pauseHandler)
			video.removeEventListener('ended', endedHandler)
			video.removeEventListener('volumechange', volumeChangeHandler)
			video.removeEventListener('loadeddata', readyHandler)
			video.removeEventListener('loadedmetadata', canPlayHandler)
			video.removeEventListener('canplay', canPlayHandler)
		}
	}, [])

	return (
		<div
			ref={containerRef}
			className={cn('group relative w-full h-full overflow-clip', className)}
			data-playback-id={playbackId}
			onMouseEnter={() => setIsHovered(true)}
			onMouseLeave={() => setIsHovered(false)}
		>
			<div className="relative w-full h-full">
				{/* Image sets the size of the video - get rid of safari black border bug!  */}
				<img
					className="block relative z-0 w-full h-auto"
					src={`https://image.mux.com/${playbackId}/thumbnail.webp?width=192`}
					alt=""
				/>

				<div className="absolute z-100 inset-[-2px] overflow-clip">
					<MuxVideoComponent
						ref={videoRef}
						playbackId={playbackId}
						className={`absolute inset-0 z-100 w-full h-full object-cover pointer-events-none`}
						playsInline
						autoPlay={false} // Always handle autoplay when in viewport, if enabled
						muted={isMuted}
						loop={loop}
						minResolution={minResolution}
						// Chrome now claims native HLS support via canPlayType but fails to play the
						// stream, so Mux picks native and errors. hls.js falls back to native on iOS.
						preferPlayback="mse"
						// set poster t oblank base64 image
						poster={`data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNk+A8AAQUBAScY42YAAAAASUVORK5CYII=`}
						// poster={
						// 	posterTime ?
						// 		`https://image.mux.com/${playbackId}/thumbnail.webp?time=${posterTime}`
						// 	:	`https://image.mux.com/${playbackId}/thumbnail.webp`
						// }
					/>
				</div>
			</div>

			{!autoplay && (
				<button
					className={`absolute inset-0 z-200 w-full h-full flex items-center justify-center`}
					onClick={togglePlayPause}
				>
					<div
						onClick={togglePlayPause}
						className="absolute inset-center z-200 bg-white dark:bg-black text-black dark:text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"
					>
						{isPlaying ?
							<PauseIcon />
						:	<PlayIcon />}
					</div>
				</button>
			)}

			{hasAudio && showAudioToggle && (
				<button
					onClick={toggleSound}
					className="absolute top-4 right-4 z-200 bg-white dark:bg-black text-black dark:text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"
				>
					{isMuted ?
						<svg
							className="size-5"
							stroke="currentColor"
							fill="currentColor"
							strokeWidth="0"
							viewBox="0 0 512 512"
							xmlns="http://www.w3.org/2000/svg"
						>
							<path d="M64 192v128h85.334L256 431.543V80.458L149.334 192H64z"></path>
						</svg>
					:	<svg
							className="size-5"
							stroke="currentColor"
							fill="currentColor"
							strokeWidth="0"
							viewBox="0 0 512 512"
							xmlns="http://www.w3.org/2000/svg"
						>
							<path d="M64 192v128h85.334L256 431.543V80.458L149.334 192H64zm288 64c0-38.399-21.333-72.407-53.333-88.863v176.636C330.667 328.408 352 294.4 352 256zM298.667 64v44.978C360.531 127.632 405.334 186.882 405.334 256c0 69.119-44.803 128.369-106.667 147.022V448C384 428.254 448 349.257 448 256c0-93.256-64-172.254-149.333-192z"></path>
						</svg>
					}
				</button>
			)}
		</div>
	)
}
